import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rumbledirect/ui-v2";
import type { VehicleConversion } from "@/configurator/vehicleConversions/vehicleConversion";
import { ConfiguratorOptionKind } from "@/configurator/vehicleConversions/vehicleConversion";
import { BuildAccessoriesTab } from "@/configurator/vehicleConversions/BuildAccessoriesTab";
import { ConversionBuildsTab } from "@/configurator/vehicleConversions/ConversionBuildsTab";
import { ConversionExclusionGroupsTab } from "@/configurator/vehicleConversions/ConversionExclusionGroupsTab";
import { ConversionOptionsTab } from "@/configurator/vehicleConversions/ConversionOptionsTab";
import { toVehicleConversionPatchInput } from "@/configurator/vehicleConversions/vehicleConversionForm";
import { VehicleConversionDetailsTab } from "@/configurator/vehicleConversions/VehicleConversionDetailsTab";
import { VehicleConversionMediaTab } from "@/configurator/vehicleConversions/VehicleConversionMediaTab";
import { useUpdateVehicleConversion } from "@/configurator/vehicleConversions/useUpdateVehicleConversion";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { useGetAdminConfiguratorVehicleConversionsVehicleConversionId } from "@/lib/api/generated/configurator/configurator";

const UPDATE_PERMISSION = "admin_configurator_vehicle_conversion_update";
/** Ties the header save button to the details form rendered inside the tabs. */
const DETAILS_FORM_ID = "vehicle-conversion-details-form";

const DETAIL_TABS = [
  "algemeen",
  "media",
  "inrichting",
  "accessoires",
  "belettering",
  "telematica",
  "aanvullend",
  "uitsluitingen",
] as const;
type DetailTab = (typeof DETAIL_TABS)[number];
const DEFAULT_TAB: DetailTab = "algemeen";

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.includes(value as DetailTab);
}

interface VehicleConversionEditSearch {
  readonly tab: DetailTab;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-conversions_/$vehicleConversionId_/edit",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): VehicleConversionEditSearch => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
  }),
  component: VehicleConversionEditPage,
});

function VehicleConversionEditPage(): ReactNode {
  const { vehicleConversionId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const user = useCurrentUser();
  const canUpdate = hasPermission(user, UPDATE_PERMISSION);

  const { data, isPending, isError, refetch } =
    useGetAdminConfiguratorVehicleConversionsVehicleConversionId(
      vehicleConversionId,
    );
  const updateMutation = useUpdateVehicleConversion(vehicleConversionId);

  const title = data?.title || "Voertuigconversie";

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/vehicle-conversions"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Terug naar voertuigconversies
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4">
        {isPending ? (
          <Skeleton className="h-8 w-64" />
        ) : (
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        )}

        {data && canUpdate && (
          <Button
            type="submit"
            form={DETAILS_FORM_ID}
            size="sm"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="size-4" aria-hidden="true" />
            )}
            Wijzigingen opslaan
          </Button>
        )}
      </div>

      {isError ? (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-muted-foreground">
            We konden deze voertuigconversie niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <VehicleConversionEditBody
          vehicleConversion={data}
          tab={tab}
          canUpdate={canUpdate}
          onTabChange={(nextTab) =>
            void navigate({ search: (prev) => ({ ...prev, tab: nextTab }) })
          }
          onSaveDetails={(values) =>
            updateMutation.mutateAsync(toVehicleConversionPatchInput(values))
          }
        />
      )}
    </div>
  );
}

interface VehicleConversionEditBodyProps {
  readonly vehicleConversion: VehicleConversion;
  readonly tab: DetailTab;
  readonly canUpdate: boolean;
  readonly onTabChange: (tab: DetailTab) => void;
  readonly onSaveDetails: (
    values: Parameters<typeof toVehicleConversionPatchInput>[0],
  ) => Promise<unknown>;
}

function VehicleConversionEditBody({
  vehicleConversion,
  tab,
  canUpdate,
  onTabChange,
  onSaveDetails,
}: VehicleConversionEditBodyProps): ReactNode {
  const id = vehicleConversion.id;
  const options = vehicleConversion.options;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        if (isDetailTab(value)) onTabChange(value);
      }}
      className="flex flex-col gap-5"
    >
      <TabsList className="self-start">
        <TabsTrigger value="algemeen">Algemeen</TabsTrigger>
        <TabsTrigger value="media">Media</TabsTrigger>
        <TabsTrigger value="inrichting">Inrichting</TabsTrigger>
        <TabsTrigger value="accessoires">Inrichting accessoires</TabsTrigger>
        <TabsTrigger value="belettering">Belettering</TabsTrigger>
        <TabsTrigger value="telematica">Telematica</TabsTrigger>
        <TabsTrigger value="aanvullend">Aanvullende werkzaamheden</TabsTrigger>
        <TabsTrigger value="uitsluitingen">Uitsluitingen</TabsTrigger>
      </TabsList>

      {/* Kept mounted (not a TabsContent) so its form state survives tab
          switches and the header save button can submit it from any tab. */}
      <div hidden={tab !== "algemeen"} role="tabpanel">
        <Card className="p-6">
          <VehicleConversionDetailsTab
            formId={DETAILS_FORM_ID}
            vehicleConversion={vehicleConversion}
            canUpdate={canUpdate}
            onSave={onSaveDetails}
          />
        </Card>
      </div>

      <TabsContent value="media">
        <Card className="p-6">
          <VehicleConversionMediaTab
            vehicleConversionId={id}
            media={vehicleConversion.media}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="inrichting">
        <Card className="p-6">
          <ConversionBuildsTab
            vehicleConversionId={id}
            buildGroups={vehicleConversion.buildGroups}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="accessoires">
        <Card className="p-6">
          <BuildAccessoriesTab
            vehicleConversionId={id}
            selectedAccessoryIds={vehicleConversion.accessories.flatMap(
              (accessory) => (accessory.id ? [accessory.id] : []),
            )}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="belettering">
        <Card className="p-6">
          <ConversionOptionsTab
            vehicleConversionId={id}
            options={options}
            optionKind={ConfiguratorOptionKind.lettering}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="telematica">
        <Card className="p-6">
          <ConversionOptionsTab
            vehicleConversionId={id}
            options={options}
            optionKind={ConfiguratorOptionKind.telematics}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="aanvullend">
        <Card className="p-6">
          <ConversionOptionsTab
            vehicleConversionId={id}
            options={options}
            optionKind={ConfiguratorOptionKind.other}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="uitsluitingen">
        <Card className="p-6">
          <ConversionExclusionGroupsTab
            vehicleConversionId={id}
            options={options}
            exclusionGroups={vehicleConversion.exclusionGroups}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>
    </Tabs>
  );
}
